import { 
  collection, 
  getDocs, 
  setDoc, 
  doc, 
  query, 
  orderBy, 
  where,
  deleteDoc,
  onSnapshot,
  getDocFromServer,
  getDocsFromCache,
  limit,
  startAfter,
  getCountFromServer,
  QueryConstraint,
  DocumentSnapshot
} from 'firebase/firestore';
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { 
  ref, 
  uploadString, 
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { db, auth, storage } from '../firebase';
import { GenerationTask, UserPermission } from '../types';

const COLLECTION_NAME = 'tasks';
const PERMISSIONS_COLLECTION = 'userPermissions';
const STORAGE_PATH = 'task-contents';

// Error Handling Spec for Firestore Operations
enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  }
  const serializedError = JSON.stringify(errInfo);
  console.error('Firestore Error: ', serializedError);
  throw new Error(serializedError);
}

// Helper to check if content is a Storage URL
const isStorageUrl = (content: string) => {
  return typeof content === 'string' && content.startsWith('https://firebasestorage.googleapis.com/');
};

// Helper to fetch content from Storage if needed
export const fetchContentIfNeeded = async (task: GenerationTask): Promise<GenerationTask> => {
  if (task.outputContent && isStorageUrl(task.outputContent)) {
    try {
      const response = await fetch(task.outputContent);
      if (response.ok) {
        const outputContent = await response.text();
        return { ...task, outputContent };
      }
    } catch (error) {
      console.error("[StorageService] Failed to fetch content from Storage:", error);
    }
  }
  return task;
};

export const signIn = async (): Promise<User> => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error("Sign in failed", error);
    throw error;
  }
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  if (!auth) {
    console.error("[StorageService] Cannot subscribe to auth changes: auth is not initialized");
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
};

// Task Operations
export const subscribeTasks = (
  callback: (tasks: GenerationTask[]) => void, 
  errorCallback?: (error: any) => void,
  allowedAgents?: string[] | null
) => {
  if (!db) {
    console.error("[StorageService] Cannot subscribe to tasks: db is not initialized");
    return () => {};
  }

  let q;
  let effectiveAllowedAgents = allowedAgents;
  
  if (effectiveAllowedAgents && !effectiveAllowedAgents.includes('all')) {
    // Filter by allowed agents if specified
    if (effectiveAllowedAgents.length === 1) {
      q = query(
        collection(db, COLLECTION_NAME),
        where("agentName", "==", effectiveAllowedAgents[0]),
        orderBy("createdAt", "desc"),
        limit(1000)
      );
    } else {
      q = query(
        collection(db, COLLECTION_NAME),
        where("agentName", "in", effectiveAllowedAgents),
        orderBy("createdAt", "desc"),
        limit(1000)
      );
    }
  } else {
    // Listen for ALL tasks
    q = query(
      collection(db, COLLECTION_NAME),
      orderBy("createdAt", "desc"),
      limit(1000)
    );
  }

  const unsubscribe = onSnapshot(q, async (snapshot) => {
    const rawTasks = snapshot.docs.map(doc => {
      const data = doc.data();
      return { ...(data as any), id: doc.id } as GenerationTask;
    });
    // Sort in memory to avoid Firestore index requirements
    const sortedTasks = rawTasks.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    const tasks = await Promise.all(sortedTasks.map(fetchContentIfNeeded));
    callback(tasks);
  }, (error) => {
    // onSnapshot errors are logged but shouldn't throw to avoid crashing react
    const errInfo = {
      error: error.message,
      operationType: OperationType.LIST,
      path: COLLECTION_NAME,
      authInfo: { userId: auth.currentUser?.uid }
    };
    console.error('Firestore Snapshot Error: ', JSON.stringify(errInfo));
    if (errorCallback) errorCallback(error);
  });

  return unsubscribe;
};

export const saveTasks = async (tasks: GenerationTask[]): Promise<void> => {
  for (const task of tasks) {
    await saveTask(task);
  }
};

export const saveTask = async (task: GenerationTask): Promise<void> => {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    console.warn("[StorageService] saveTask called without authenticated user. Skipping.");
    return;
  }
  const path = `${COLLECTION_NAME}/${task.id}`;
  
  try {
    const now = Date.now();
    let taskToSave = { 
      ...task, 
      userId,
      updatedAt: now,
      createdAt: task.createdAt || now
    };
    
    // Check size (approx 1MB limit for Firestore)
    const taskSize = JSON.stringify(taskToSave).length;
    if (taskSize > 800000 && taskToSave.outputContent) {
      console.log(`[StorageService] Task ${task.id} is large (${taskSize} bytes). Moving content to Storage...`);
      if (!storage) {
        throw new Error("Firebase Storage is not initialized. Check your firebase-applet-config.json for storageBucket.");
      }
      const storageRef = ref(storage, `${STORAGE_PATH}/${task.id}.txt`);
      // Use blob for better handling of large strings
      const blob = new Blob([taskToSave.outputContent], { type: 'text/plain' });
      const uploadResult = await uploadString(storageRef, taskToSave.outputContent);
      const contentUrl = await getDownloadURL(storageRef);
      taskToSave.outputContent = contentUrl;
      // Mark that content is in storage so we know to fetch it
      taskToSave.isContentInStorage = true;
    }

    await setDoc(doc(db, COLLECTION_NAME, task.id), taskToSave);
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, path);
  }
};

export const loadTasks = async (allowedAgents?: string[] | null): Promise<GenerationTask[]> => {
  const userId = auth.currentUser?.uid;
  if (!userId) return [];
  
  let effectiveAllowedAgents = allowedAgents;

  const path = COLLECTION_NAME;
  try {
    let q;
    if (effectiveAllowedAgents && !effectiveAllowedAgents.includes('all')) {
      if (effectiveAllowedAgents.length === 1) {
        q = query(
          collection(db, COLLECTION_NAME), 
          where("agentName", "==", effectiveAllowedAgents[0]),
          orderBy("createdAt", "desc"),
          limit(1000)
        );
      } else {
        q = query(
          collection(db, COLLECTION_NAME), 
          where("agentName", "in", effectiveAllowedAgents),
          orderBy("createdAt", "desc"),
          limit(1000)
        );
      }
    } else {
      q = query(
        collection(db, COLLECTION_NAME),
        orderBy("createdAt", "desc"),
        limit(1000)
      );
    }

    const querySnapshot = await getDocs(q);
    const rawTasks = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return { ...(data as any), id: doc.id } as GenerationTask;
    });
    // Sort in memory
    const sortedTasks = rawTasks.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    
    // Fetch content from Storage if needed
    return await Promise.all(sortedTasks.map(fetchContentIfNeeded));
  } catch (error) {
    handleFirestoreError(error, OperationType.LIST, path);
    return [];
  }
};

export const deleteTask = async (taskId: string): Promise<void> => {
  console.log(`[StorageService] deleteTask called for: ${taskId}`);
  const path = `${COLLECTION_NAME}/${taskId}`;
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, taskId));
    console.log(`[StorageService] Firestore document deleted: ${taskId}`);
    // Also delete from Storage if it exists
    if (storage) {
      try {
        const storageRef = ref(storage, `${STORAGE_PATH}/${taskId}.txt`);
        await deleteObject(storageRef);
        console.log(`[StorageService] Storage object deleted: ${taskId}`);
      } catch (e) {
        // Ignore if not in storage
      }
    }
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, path);
  }
};

export const uploadImage = async (taskId: string, file: File): Promise<string> => {
  if (!storage) {
    throw new Error("Firebase Storage is not initialized.");
  }
  
  const fileName = `${Date.now()}_${file.name}`;
  const storageRef = ref(storage, `tasks/${taskId}/images/${fileName}`);
  
  try {
    await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(storageRef);
    return downloadUrl;
  } catch (error) {
    console.error("[StorageService] Image upload failed:", error);
    throw error;
  }
};

export const deleteImage = async (imageUrl: string): Promise<void> => {
  if (!storage) return;
  try {
    const storageRef = ref(storage, imageUrl);
    await deleteObject(storageRef);
  } catch (error) {
    console.error("[StorageService] Image deletion failed:", error);
  }
};

// Permission Operations
export const getUserPermission = async (email: string): Promise<UserPermission | null> => {
  if (!db) return null;
  try {
    const docSnap = await getDocFromServer(doc(db, PERMISSIONS_COLLECTION, email));
    if (docSnap.exists()) {
      return { ...docSnap.data(), id: docSnap.id } as UserPermission;
    }
    return null;
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, PERMISSIONS_COLLECTION);
    return null;
  }
};

export const saveUserPermission = async (permission: UserPermission): Promise<void> => {
  if (!db) return;
  try {
    await setDoc(doc(db, PERMISSIONS_COLLECTION, permission.email), {
      ...permission,
      updatedAt: Date.now()
    });
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, PERMISSIONS_COLLECTION);
  }
};

export const getAllUserPermissions = async (): Promise<UserPermission[]> => {
  if (!db) return [];
  try {
    const q = query(collection(db, PERMISSIONS_COLLECTION), orderBy("updatedAt", "desc"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as UserPermission));
  } catch (error) {
    handleFirestoreError(error, OperationType.LIST, PERMISSIONS_COLLECTION);
    return [];
  }
};

export const deleteUserPermission = async (email: string): Promise<void> => {
  if (!db) return;
  try {
    await deleteDoc(doc(db, PERMISSIONS_COLLECTION, email));
  } catch (error) {
    handleFirestoreError(error, OperationType.DELETE, PERMISSIONS_COLLECTION);
  }
};
